import { ResponsiveBar, type BarCustomLayerProps } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import { useTranslate } from "ra-core";

import { useDateLocale } from "../../personal/useDateLocale";
import type {
  FinanceForecastPoint,
  FinanceMonthPoint,
} from "../../providers/commons/financeMetrics";
import { roundMoney } from "../../providers/commons/financeSchedule";
import { useFormatMoney } from "../useFormatMoney";
import { ChartCard } from "./ChartCard";
import { ChartTooltip } from "./ChartTooltip";
import { useFinanceColors } from "./useFinanceColors";
import { BAR_GAP, BAR_RADIUS, monthLabel, buildNivoTheme } from "./chartTheme";

// Room for compact currency ticks ("R$ 20 mil") and the last month label
const MARGIN = { top: 10, right: 20, bottom: 28, left: 76 };

type BarDatum = { month: string } & Record<string, string | number>;

interface SeriesSpec {
  key: string;
  label: string;
  color: string;
}

const useMonthFormat = () => {
  const { dateFnsLocale } = useDateLocale();
  return (month: string) => monthLabel(month, dateFnsLocale);
};

/** Bars per month, grouped or stacked, one color per entity */
const MonthlyBars = ({
  data,
  series,
  groupMode,
  layers,
  minValue = 0,
}: {
  data: BarDatum[];
  series: SeriesSpec[];
  groupMode: "grouped" | "stacked";
  layers?: Array<(props: BarCustomLayerProps<BarDatum>) => React.ReactNode>;
  /** Lowest value of the scale, below 0 when a layer draws negative values */
  minValue?: number;
}) => {
  const formatMoney = useFormatMoney();
  const colors = useFinanceColors();
  const colorOf = new Map(series.map((item) => [item.key, item.color]));
  return (
    <ResponsiveBar<BarDatum>
      data={data}
      keys={series.map((item) => item.key)}
      indexBy="month"
      groupMode={groupMode}
      margin={MARGIN}
      padding={0.3}
      innerPadding={BAR_GAP}
      borderRadius={BAR_RADIUS}
      colors={({ id }) => colorOf.get(String(id)) ?? series[0].color}
      valueScale={{ type: "linear", nice: true, min: minValue }}
      enableLabel={false}
      enableGridY
      axisBottom={{ tickSize: 0, tickPadding: 8 }}
      axisLeft={{
        tickSize: 0,
        tickPadding: 6,
        tickValues: 5,
        format: (value) => formatMoney(Number(value), true),
      }}
      theme={buildNivoTheme(colors)}
      tooltip={({ indexValue, data: datum }) => (
        <ChartTooltip
          title={String(datum.label ?? indexValue)}
          lines={series.map((item) => ({
            label: item.label,
            value: formatMoney(Number(datum[item.key] ?? 0)),
            color: item.color,
          }))}
        />
      )}
      layers={["grid", "axes", "bars", ...(layers ?? [])]}
    />
  );
};

const toTable = (
  firstColumn: string,
  series: SeriesSpec[],
  data: BarDatum[],
  formatMoney: (amount: number) => string,
) => ({
  columns: [firstColumn, ...series.map((item) => item.label)],
  rows: data.map((datum) => ({
    key: String(datum.month),
    cells: [
      String(datum.label),
      ...series.map((item) => formatMoney(Number(datum[item.key] ?? 0))),
    ],
  })),
});

export const MrrChart = ({ monthly }: { monthly: FinanceMonthPoint[] }) => {
  const translate = useTranslate();
  const formatMoney = useFormatMoney();
  const formatMonth = useMonthFormat();
  const colors = useFinanceColors();
  const label = translate("crm.finance.overview.series.mrr");
  const points = monthly.map((point) => ({
    x: formatMonth(point.month),
    y: point.mrr,
  }));
  return (
    <ChartCard
      title={translate("crm.finance.overview.charts.mrr")}
      description={translate("crm.finance.overview.charts.mrr_help")}
      table={{
        columns: [translate("crm.finance.overview.month"), label],
        rows: points.map((point) => ({
          key: point.x,
          cells: [point.x, formatMoney(point.y)],
        })),
      }}
      isEmpty={monthly.every((point) => point.mrr === 0)}
    >
      <ResponsiveLine
        data={[{ id: "mrr", data: points }]}
        margin={MARGIN}
        xScale={{ type: "point" }}
        yScale={{ type: "linear", min: 0, max: "auto", nice: true }}
        colors={[colors.recurring]}
        lineWidth={2}
        enableArea
        areaOpacity={0.08}
        pointSize={8}
        pointColor={colors.surface}
        pointBorderWidth={2}
        pointBorderColor={{ from: "seriesColor" }}
        enableGridX={false}
        axisBottom={{ tickSize: 0, tickPadding: 8 }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 6,
          tickValues: 5,
          format: (value) => formatMoney(Number(value), true),
        }}
        theme={buildNivoTheme(colors)}
        enableSlices="x"
        sliceTooltip={({ slice }) => (
          <ChartTooltip
            title={String(slice.points[0]?.data.x ?? "")}
            lines={slice.points.map((point) => ({
              label,
              value: formatMoney(Number(point.data.y)),
              color: colors.recurring,
            }))}
          />
        )}
      />
    </ChartCard>
  );
};

export const BilledReceivedChart = ({
  monthly,
}: {
  monthly: FinanceMonthPoint[];
}) => {
  const translate = useTranslate();
  const formatMoney = useFormatMoney();
  const formatMonth = useMonthFormat();
  const colors = useFinanceColors();
  const series: SeriesSpec[] = [
    {
      key: "billed",
      label: translate("crm.finance.overview.series.billed"),
      color: colors.recurring,
    },
    {
      key: "received",
      label: translate("crm.finance.overview.series.received"),
      color: colors.received,
    },
  ];
  const data = monthly.map((point) => ({
    month: point.month,
    label: formatMonth(point.month),
    billed: point.billed,
    received: point.received,
  }));
  return (
    <ChartCard
      title={translate("crm.finance.overview.charts.billed_received")}
      description={translate(
        "crm.finance.overview.charts.billed_received_help",
      )}
      legend={series}
      table={toTable(
        translate("crm.finance.overview.month"),
        series,
        data,
        formatMoney,
      )}
      isEmpty={monthly.every(
        (point) => point.billed === 0 && point.received === 0,
      )}
    >
      <MonthlyBars
        data={data.map((datum) => ({ ...datum, month: datum.label }))}
        series={series}
        groupMode="grouped"
      />
    </ChartCard>
  );
};

/** Line of the monthly result drawn over the revenue / expenses bars */
const NetLayer = ({
  bars,
  yScale,
}: BarCustomLayerProps<BarDatum>): React.ReactNode => {
  const byMonth = new Map<string, { x: number; net: number }>();
  bars.forEach((bar) => {
    const center = bar.x + bar.width / 2;
    const current = byMonth.get(String(bar.data.indexValue));
    const net = Number(bar.data.data.net ?? 0);
    // Center of the group: average of the bars of the month
    byMonth.set(String(bar.data.indexValue), {
      x: current ? (current.x + center) / 2 : center,
      net,
    });
  });
  const points = [...byMonth.values()].map((point) => ({
    x: point.x,
    y: (yScale as (value: number) => number)(point.net),
  }));
  if (points.length === 0) return null;
  return (
    <g>
      <path
        d={points
          .map((point, index) => `${index ? "L" : "M"}${point.x},${point.y}`)
          .join(" ")}
        fill="none"
        stroke="var(--color-foreground)"
        strokeWidth={2}
      />
      {points.map((point) => (
        <circle
          key={point.x}
          cx={point.x}
          cy={point.y}
          r={4}
          fill="var(--color-card)"
          stroke="var(--color-foreground)"
          strokeWidth={2}
        />
      ))}
    </g>
  );
};

export const ResultChart = ({ monthly }: { monthly: FinanceMonthPoint[] }) => {
  const translate = useTranslate();
  const formatMoney = useFormatMoney();
  const formatMonth = useMonthFormat();
  const colors = useFinanceColors();
  const series: SeriesSpec[] = [
    {
      key: "received",
      label: translate("crm.finance.overview.series.received"),
      color: colors.received,
    },
    {
      key: "expenses",
      label: translate("crm.finance.overview.series.expenses_paid"),
      color: colors.expenses,
    },
  ];
  const netLabel = translate("crm.finance.overview.series.net");
  const data = monthly.map((point) => ({
    month: formatMonth(point.month),
    label: formatMonth(point.month),
    received: point.received,
    expenses: point.expensesPaid,
    net: point.net,
  }));
  return (
    <ChartCard
      title={translate("crm.finance.overview.charts.result")}
      description={translate("crm.finance.overview.charts.result_help")}
      legend={[
        ...series,
        { label: netLabel, color: "var(--color-foreground)", isLine: true },
      ]}
      table={toTable(
        translate("crm.finance.overview.month"),
        [...series, { key: "net", label: netLabel, color: "" }],
        data,
        formatMoney,
      )}
      isEmpty={monthly.every(
        (point) => point.received === 0 && point.expensesPaid === 0,
      )}
    >
      <MonthlyBars
        data={data}
        series={series}
        groupMode="grouped"
        layers={[NetLayer]}
        minValue={Math.min(0, ...data.map((datum) => datum.net))}
      />
    </ChartCard>
  );
};

export const RevenueMixChart = ({
  monthly,
}: {
  monthly: FinanceMonthPoint[];
}) => {
  const translate = useTranslate();
  const formatMoney = useFormatMoney();
  const formatMonth = useMonthFormat();
  const colors = useFinanceColors();
  const series: SeriesSpec[] = [
    {
      key: "recurring",
      label: translate("crm.finance.overview.series.recurring"),
      color: colors.recurring,
    },
    {
      key: "setup",
      label: translate("crm.finance.overview.series.setup"),
      color: colors.setup,
    },
  ];
  const data = monthly.map((point) => ({
    month: formatMonth(point.month),
    label: formatMonth(point.month),
    recurring: point.recurringReceived,
    setup: point.setupReceived,
  }));
  return (
    <ChartCard
      title={translate("crm.finance.overview.charts.mix")}
      description={translate("crm.finance.overview.charts.mix_help")}
      legend={series}
      table={toTable(
        translate("crm.finance.overview.month"),
        series,
        data,
        formatMoney,
      )}
      isEmpty={monthly.every((point) => point.received === 0)}
    >
      <MonthlyBars data={data} series={series} groupMode="stacked" />
    </ChartCard>
  );
};

export const ForecastChart = ({
  forecast,
}: {
  forecast: FinanceForecastPoint[];
}) => {
  const translate = useTranslate();
  const formatMoney = useFormatMoney();
  const formatMonth = useMonthFormat();
  const colors = useFinanceColors();
  const series: SeriesSpec[] = [
    {
      key: "recurring",
      label: translate("crm.finance.overview.series.recurring"),
      color: colors.recurring,
    },
    {
      key: "setup",
      label: translate("crm.finance.overview.series.setup"),
      color: colors.setup,
    },
  ];
  // One-off charges are rare in a forecast: they join the recurring revenue
  const data = forecast.map((point) => ({
    month: formatMonth(point.month),
    label: formatMonth(point.month),
    recurring: roundMoney(point.recurring + point.oneOff),
    setup: point.setup,
  }));
  return (
    <ChartCard
      title={translate("crm.finance.overview.charts.forecast")}
      description={translate("crm.finance.overview.charts.forecast_help")}
      legend={series}
      table={toTable(
        translate("crm.finance.overview.month"),
        series,
        data,
        formatMoney,
      )}
      isEmpty={data.every(
        (point) => point.recurring === 0 && point.setup === 0,
      )}
    >
      <MonthlyBars data={data} series={series} groupMode="stacked" />
    </ChartCard>
  );
};
